import React, { useState } from "react";
import { useLang } from "../../i18n/LanguageContext";
import { sendContact } from "../../lib/api";
import { Loader2, Check, ArrowRight } from "lucide-react";
import CalendlyEmbed from "../CalendlyEmbed";

const FinalCtaContact = () => {
  const { t, lang } = useLang();
  const topics = t("contact.topic_options");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", topic: topics[0], message: "", datenschutz_ok: false, newsletter_opt_in: false });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [showCal, setShowCal] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.datenschutz_ok) {
      setStatus("err");
      setErr(lang === "en" ? "Please confirm the privacy notice." : "Bitte bestätige die Datenschutzhinweise.");
      return;
    }
    setStatus("loading"); setErr("");
    try {
      await sendContact({ ...form, language: lang });
      setStatus("ok");
    } catch (e) {
      setStatus("err");
      setErr(e?.response?.data?.detail || "Error");
    }
  };
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  return (
    <section id="kontakt" className="relative py-32 lg:py-48 bg-[#040814]" data-testid="finalcta-section">
      <div className="container-sat grid lg:grid-cols-12 gap-16 items-start">
        <div className="lg:col-span-7">
          <div className="overline mb-6">·</div>
          <h2 className="headline-xl text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.9]" data-testid="finalcta-title">
            <span className="block text-white">{t("finalcta.title_l1")}</span>
            <span className="block headline-italic">{t("finalcta.title_l2")}</span>
            <span className="block text-white">{t("finalcta.title_l3")}</span>
          </h2>
          <div className="mt-10 flex items-center gap-3 text-[#D4AF37] tracking-widest text-xs uppercase">
            <span className="w-12 h-px bg-[#D4AF37]"></span>
            {t("finalcta.whisper")}
          </div>
          <p className="serif italic text-white/55 text-xl mt-10 max-w-md">{t("contact.subtitle")}</p>
          <div className="mt-6">
            <button onClick={() => setShowCal(!showCal)} className="btn-secondary text-sm" data-testid="toggle-calendly">
              {showCal ? "Kontaktformular anzeigen" : "Termin direkt buchen"} <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div className="lg:col-span-5">
          {showCal ? (
            <div data-testid="calendly-wrapper"><CalendlyEmbed /></div>
          ) : (
          <div className="glass rounded-3xl p-8 md:p-10" data-testid="contact-card">
            <div className="serif text-3xl mb-2 font-light">{t("contact.title")}</div>
            {status === "ok" ? (
              <div className="py-8 text-center" data-testid="contact-success">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37] text-[#081328] flex items-center justify-center mb-4">
                  <Check size={26} />
                </div>
                <p className="serif text-2xl text-white">{t("contact.success")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3 mt-6" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-3">
                  <input required className="sat-input" placeholder={t("contact.first_name")} value={form.first_name} onChange={update("first_name")} data-testid="contact-first" />
                  <input className="sat-input" placeholder={t("contact.last_name")} value={form.last_name} onChange={update("last_name")} data-testid="contact-last" />
                </div>
                <input required type="email" className="sat-input" placeholder={t("contact.email")} value={form.email} onChange={update("email")} data-testid="contact-email" />
                <input className="sat-input" placeholder={t("contact.phone")} value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                <select className="sat-input" value={form.topic} onChange={update("topic")} data-testid="contact-topic">
                  {topics.map((tp, i) => <option key={i} value={tp}>{tp}</option>)}
                </select>
                <textarea required className="sat-input" placeholder={t("contact.message")} value={form.message} onChange={update("message")} data-testid="contact-message" />
                {/* Block 1 (Mastermatrix v1.1) · verpflichtende, NICHT vorangekreuzte Datenschutz-Einwilligung */}
                <label className="flex items-start gap-3 text-xs text-white/65 leading-relaxed pt-1" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    checked={form.datenschutz_ok}
                    onChange={toggle("datenschutz_ok")}
                    className="mt-0.5 accent-[#C7A45B]"
                    data-testid="contact-datenschutz"
                    required
                  />
                  <span>
                    {lang === "en"
                      ? <>I have read the <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#C7A45B] underline">privacy notice</a> and agree that my data is processed to answer this request.</>
                      : <>Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#C7A45B] underline">Datenschutzhinweise</a> gelesen und stimme zu, dass meine Angaben zur Beantwortung meiner Anfrage verarbeitet werden.</>}
                  </span>
                </label>
                {/* Optionaler, getrennter Newsletter-Opt-in */}
                <label className="flex items-start gap-3 text-xs text-white/55 leading-relaxed" data-testid="contact-newsletter-label">
                  <input
                    type="checkbox"
                    checked={form.newsletter_opt_in}
                    onChange={toggle("newsletter_opt_in")}
                    className="mt-0.5 accent-[#C7A45B]"
                    data-testid="contact-newsletter-opt-in"
                  />
                  <span>
                    {lang === "en"
                      ? "Optional: send me occasional letters from Nenad and SAT. I can unsubscribe at any time."
                      : "Optional: Schicke mir gelegentlich Briefe von Nenad und SAT. Ich kann mich jederzeit abmelden."}
                  </span>
                </label>
                <button type="submit" disabled={status === "loading"} className="btn-primary w-full justify-center" data-testid="contact-submit">
                  {status === "loading" ? <Loader2 className="animate-spin" size={18} /> : (<>{t("finalcta.cta")} <ArrowRight size={16} /></>)}
                </button>
                {status === "err" && <p className="text-red-300 text-xs">{err}</p>}
              </form>
            )}
          </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FinalCtaContact;
